import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, ArrowLeft, Users, Check, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animation variants
const fadeInLeft = { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 } };
const fadeInRight = { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 } };
const fadeInUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

const mascots = [
  { id: 'Mickey', name: 'Mickey Mouse', color: 'bg-red-500' },
  { id: 'Minnie', name: 'Minnie Mouse', color: 'bg-pink-500' },
  { id: 'Peppa Pig', name: 'Peppa Pig', color: 'bg-pink-400' },
  { id: 'Blue Eye', name: 'Blue Eye', color: 'bg-blue-500' },
  { id: 'Spiderman', name: 'Spiderman', color: 'bg-red-600' },
  { id: 'Chase', name: 'Chase (Paw Patrol)', color: 'bg-blue-600' },
  { id: 'Skye', name: 'Skye (Paw Patrol)', color: 'bg-pink-300' },
  { id: 'Cocomelon', name: 'Cocomelon', color: 'bg-green-500' },
  { id: 'Lady Bug', name: 'Lady Bug', color: 'bg-red-500' },
  { id: 'Elsa', name: 'Elsa (Frozen)', color: 'bg-cyan-400' },
  { id: 'Easter Rabbit', name: 'Easter Rabbit', color: 'bg-purple-400' }
];

const MascotBookingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [selectedMascot, setSelectedMascot] = useState(null);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile_phone: '',
    event_details: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !selectedMascot) {
      toast.error(t('mascotBooking.selectDateAndMascot'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/bookings/mascot`, {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        mascot: selectedMascot
      });
      
      toast.success(t('mascotBooking.successTitle'), {
        description: t('mascotBooking.successDescription')
      });
      
      // Reset form
      setFormData({ name: '', mobile_phone: '', event_details: '' });
      setDate(null);
      setSelectedMascot(null);
      
    } catch (error) {
      toast.error(t('mascotBooking.errorTitle'), {
        description: error.response?.data?.detail || t('mascotBooking.errorDescription')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream">
      <Header />
      
      <main className="pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-brand-blue transition-colors mb-8 font-body"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('mascotBooking.backToHome')}
          </motion.button>

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-blue/10 mb-4">
              <Users className="w-8 h-8 text-brand-blue" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('mascotBooking.title')}
            </h1>
            <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
              {t('mascotBooking.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Mascot Selection */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-2xl font-bold text-gray-900">
                {t('mascotBooking.chooseMascot')}
              </h2>
              
              <div className="grid grid-cols-3 gap-4">
                {mascots.map((mascot) => (
                  <div
                    key={mascot.id}
                    data-testid={`mascot-${mascot.id.toLowerCase().replace(' ', '-')}`}
                    onClick={() => setSelectedMascot(mascot.id)}
                    className={cn(
                      "relative p-4 rounded-2xl border-2 cursor-pointer transition-all text-center",
                      selectedMascot === mascot.id
                        ? "border-brand-blue bg-brand-blue/5 shadow-lg"
                        : "border-gray-200 bg-white hover:border-brand-blue/50 hover:shadow-md"
                    )}
                  >
                    {selectedMascot === mascot.id && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand-blue flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    
                    {/* Avatar */}
                    <div className={cn(
                      "w-16 h-16 mx-auto rounded-full flex items-center justify-center text-white font-heading font-bold text-2xl mb-3",
                      mascot.color
                    )}>
                      {mascot.name.charAt(0)}
                    </div>
                    
                    <h3 className="font-heading font-bold text-sm text-gray-900 leading-tight">
                      {mascot.name}
                    </h3>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Booking Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">
                  {t('mascotBooking.yourDetails')}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-heading font-bold text-gray-700">
                      {t('mascotBooking.fullName')}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="mascot-name-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('mascotBooking.fullNamePlaceholder')}
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-brand-blue focus:ring-brand-blue/20"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="mobile_phone" className="font-heading font-bold text-gray-700">
                      {t('mascotBooking.mobilePhone')}
                    </Label>
                    <Input
                      id="mobile_phone"
                      name="mobile_phone"
                      data-testid="mascot-phone-input"
                      value={formData.mobile_phone}
                      onChange={handleInputChange}
                      placeholder="+1 (234) 567-890"
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-brand-blue focus:ring-brand-blue/20"
                    />
                  </div>

                  {/* Date Picker */}
                  <div className="space-y-2">
                    <Label className="font-heading font-bold text-gray-700">
                      {t('mascotBooking.eventDate')}
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="mascot-date-picker"
                          className={cn(
                            "w-full h-12 justify-start text-left font-normal rounded-xl border-gray-200",
                            !date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : t('mascotBooking.selectDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Event Details */}
                  <div className="space-y-2">
                    <Label htmlFor="event_details" className="font-heading font-bold text-gray-700">
                      {t('mascotBooking.eventDetails')}
                    </Label>
                    <Textarea
                      id="event_details"
                      name="event_details"
                      data-testid="mascot-details-input"
                      value={formData.event_details}
                      onChange={handleInputChange}
                      placeholder={t('mascotBooking.eventDetailsPlaceholder')}
                      rows={4}
                      className="rounded-xl border-gray-200 focus:border-brand-blue focus:ring-brand-blue/20"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    data-testid="mascot-submit-btn"
                    disabled={loading || !selectedMascot}
                    className="w-full h-14 bg-brand-blue hover:bg-blue-600 text-white rounded-full font-heading font-bold text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {t('mascotBooking.booking')}
                      </>
                    ) : (
                      <>
                        <Users className="w-5 h-5 mr-2" />
                        {t('mascotBooking.reserveMascot')}
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MascotBookingPage;
